/**
 * SKU / MPN / spec search for the header search modal.
 *
 * Why this exists: Shopify's predictive search only matches title, description,
 * vendor, type and tags. It does NOT match variant SKU, and it does not match
 * metafields at all — so the number printed on our own product cards
 * (specs.mpn, e.g. "ADP-1/2-3/4NPT") finds nothing, and neither does a spec
 * value like "Damp Listed". Measured against /search/suggest.json before this
 * was written; see the project notes.
 *
 * Design, in two halves:
 *
 *   1. A small local index (title, handle, SKU, MPN, a few spec values) built
 *      once from the Storefront API and cached in sessionStorage. 295 products
 *      is ~54 KB, so this is cheap. Used ONLY to work out which handles match.
 *
 *   2. Every rendered card is fetched live by handle. The index can go stale
 *      between Plytix syncs; the live fetch means a stale hit either renders
 *      current data or is dropped because it no longer resolves. Nothing
 *      cached is ever shown to a shopper.
 *
 * Staleness is caught by a watermark: one tiny query for the most recently
 * updated product. If that timestamp moved, the index is rebuilt. A Plytix sync
 * bumps it, so a sync cannot slip past.
 *
 * This is additive. It renders into its own container beside Shopify's results,
 * never inside them — predictive-search.js morphs that subtree on every
 * keystroke and would wipe anything we injected. If the API is unreachable or
 * the token is missing, this stays silent and the native results are unaffected.
 */

(() => {
  const CONFIG = window.LPPStorefrontSearch || {};
  if (!CONFIG.token || !CONFIG.shop) return;

  const API = `https://${CONFIG.shop}/api/2025-01/graphql.json`;
  const CACHE_KEY = 'lpp:sf-index:v1';
  const MAX_RESULTS = 6;
  const DEBOUNCE_MS = 180;
  // Don't re-check the watermark on every keystroke — once per this window.
  const FRESHNESS_TTL_MS = 5 * 60 * 1000;

  /** Spec metafields worth searching. Kept short: every extra field is weight
   *  in the index for a value shoppers rarely type. */
  const SPEC_KEYS = [
    'series', 'wattage', 'lumens', 'cct', 'cri', 'voltage', 'finish',
    'environmental_rating', 'lens_type', 'mounting_type', 'light_distribution',
    'surge_protection',
  ];

  let indexPromise = null;
  let lastFreshnessCheck = 0;

  async function gql(query, variables) {
    const res = await fetch(API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': CONFIG.token,
      },
      body: JSON.stringify({ query, variables: variables || {} }),
    });
    if (!res.ok) throw new Error(`Storefront API ${res.status}`);
    const body = await res.json();
    if (body.errors?.length) throw new Error(body.errors[0].message);
    return body.data;
  }

  /** Timestamp of the most recently updated product — the cache watermark. */
  async function currentWatermark() {
    const d = await gql(`{
      products(first: 1, sortKey: UPDATED_AT, reverse: true) {
        nodes { updatedAt }
      }
    }`);
    return d.products.nodes[0]?.updatedAt || '';
  }

  async function buildIndex() {
    const specFields = SPEC_KEYS.map(
      (k) => `${k}: metafield(namespace: "specs", key: "${k}") { value }`
    ).join('\n');

    const QUERY = `
      query($cursor: String) {
        products(first: 250, after: $cursor) {
          pageInfo { hasNextPage endCursor }
          nodes {
            title
            handle
            vendor
            ${specFields}
            variants(first: 50) {
              nodes {
                sku
                mpn: metafield(namespace: "specs", key: "mpn") { value }
              }
            }
          }
        }
      }`;

    const rows = [];
    let cursor = null;

    for (;;) {
      const d = await gql(QUERY, { cursor });
      const page = d.products;

      for (const p of page.nodes) {
        const skus = [];
        const mpns = [];
        for (const v of p.variants.nodes) {
          if (v.sku) skus.push(v.sku);
          if (v.mpn?.value) mpns.push(v.mpn.value);
        }
        const specs = SPEC_KEYS.map((k) => p[k]?.value).filter(Boolean);

        rows.push({
          t: p.title,
          h: p.handle,
          v: p.vendor || '',
          s: skus,
          m: mpns,
          p: specs,
        });
      }

      if (!page.pageInfo.hasNextPage) break;
      cursor = page.pageInfo.endCursor;
    }

    return rows;
  }

  function readCache() {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  function writeCache(payload) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(payload));
    } catch {
      /* private mode or quota — the index still works for this page view */
    }
  }

  /**
   * Resolve the index, rebuilding when the watermark has moved. Concurrent
   * callers share one promise so a fast typist cannot start several builds.
   */
  function getIndex() {
    if (indexPromise) return indexPromise;

    indexPromise = (async () => {
      const cached = readCache();
      const now = Date.now();
      const checkedRecently = now - lastFreshnessCheck < FRESHNESS_TTL_MS;

      if (cached?.rows?.length && checkedRecently) return cached.rows;

      let watermark = '';
      try {
        watermark = await currentWatermark();
        lastFreshnessCheck = now;
      } catch {
        // Can't check — better to serve a possibly-stale index than nothing,
        // since every hit is re-fetched live before it is rendered anyway.
        if (cached?.rows?.length) return cached.rows;
        throw new Error('freshness check failed with no cache to fall back on');
      }

      if (cached?.rows?.length && cached.watermark === watermark) return cached.rows;

      const rows = await buildIndex();
      writeCache({ watermark, rows });
      return rows;
    })().catch((err) => {
      indexPromise = null; // let a later keystroke retry
      throw err;
    });

    return indexPromise;
  }

  /** Loose match: ignore case and the punctuation SKUs are full of. */
  const normalise = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

  /**
   * Score a row against the query. Identifier matches outrank text matches,
   * because someone typing "GKOHBG8" wants that fixture, not every product
   * whose description happens to contain the term.
   */
  function score(row, rawTerm, normTerm) {
    let best = 0;

    for (const sku of row.s) {
      const n = normalise(sku);
      if (n === normTerm) return 100;
      if (n.startsWith(normTerm)) best = Math.max(best, 80);
      else if (n.includes(normTerm)) best = Math.max(best, 60);
    }

    for (const mpn of row.m) {
      const n = normalise(mpn);
      if (n === normTerm) return 95;
      if (n.startsWith(normTerm)) best = Math.max(best, 75);
      else if (n.includes(normTerm)) best = Math.max(best, 55);
    }

    const title = row.t.toLowerCase();
    if (title.includes(rawTerm)) best = Math.max(best, 40);

    for (const spec of row.p) {
      if (String(spec).toLowerCase().includes(rawTerm)) {
        best = Math.max(best, 25);
        break;
      }
    }

    return best;
  }

  function match(rows, term) {
    const rawTerm = term.trim().toLowerCase();
    const normTerm = normalise(term);
    if (normTerm.length < 2) return [];

    return rows
      .map((row) => ({ row, s: score(row, rawTerm, normTerm) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s || a.row.t.localeCompare(b.row.t))
      .slice(0, MAX_RESULTS)
      .map((x) => x.row);
  }

  /**
   * Re-fetch the candidates live. This is what keeps a stale index harmless:
   * anything unpublished or deleted comes back null and is dropped, and prices
   * and images are always current.
   */
  async function hydrate(rows) {
    if (!rows.length) return [];

    const aliases = rows
      .map((r, i) => `p${i}: productByHandle(handle: ${JSON.stringify(r.h)}) {
          title
          handle
          onlineStoreUrl
          featuredImage { url(transform: { maxWidth: 120, maxHeight: 120 }) altText }
          priceRange { minVariantPrice { amount currencyCode } }
          variants(first: 1) { nodes { sku } }
        }`)
      .join('\n');

    const data = await gql(`{ ${aliases} }`);

    return rows
      .map((r, i) => ({ live: data[`p${i}`], indexed: r }))
      .filter((x) => x.live)
      .map((x) => x.live);
  }

  const money = (price) => {
    if (!price) return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: price.currencyCode,
      }).format(Number(price.amount));
    } catch {
      return `${price.amount} ${price.currencyCode}`;
    }
  };

  function render(container, products, term) {
    if (!products.length) {
      container.hidden = true;
      container.innerHTML = '';
      return;
    }

    const items = products
      .map((p) => {
        const url = p.onlineStoreUrl || `/products/${p.handle}`;
        const img = p.featuredImage
          ? `<img src="${p.featuredImage.url}" alt="${(p.featuredImage.altText || '').replace(/"/g, '&quot;')}" width="48" height="48" loading="lazy">`
          : '<span class="sf-search__thumb-placeholder" aria-hidden="true"></span>';
        const sku = p.variants.nodes[0]?.sku || '';
        return `
          <li class="sf-search__item">
            <a class="sf-search__link" href="${url}">
              <span class="sf-search__thumb">${img}</span>
              <span class="sf-search__text">
                <span class="sf-search__title">${p.title}</span>
                ${sku ? `<span class="sf-search__sku">${sku}</span>` : ''}
              </span>
              <span class="sf-search__price">${money(p.priceRange?.minVariantPrice)}</span>
            </a>
          </li>`;
      })
      .join('');

    container.innerHTML = `
      <p class="sf-search__heading">Matching SKUs &amp; specs for &ldquo;${term.replace(/</g, '&lt;')}&rdquo;</p>
      <ul class="sf-search__list">${items}</ul>`;
    container.hidden = false;
  }

  function mount(root) {
    const input = root.querySelector('input.search-input');
    const wrapper = root.querySelector('.predictive-search-form__content-wrapper');
    if (!input || !wrapper || wrapper.querySelector('.sf-search')) return;

    const container = document.createElement('div');
    container.className = 'sf-search';
    container.hidden = true;
    // Before Shopify's results container, which predictive-search.js morphs.
    wrapper.insertBefore(container, wrapper.firstChild);

    let timer = null;
    let generation = 0;

    input.addEventListener('input', () => {
      const term = input.value;
      clearTimeout(timer);

      if (term.trim().length < 2) {
        render(container, [], term);
        return;
      }

      timer = setTimeout(async () => {
        const mine = ++generation;
        try {
          const rows = await getIndex();
          const candidates = match(rows, term);
          const live = await hydrate(candidates);
          if (mine !== generation) return; // a newer keystroke won
          render(container, live, term);
        } catch (err) {
          if (mine !== generation) return;
          // Stay quiet: Shopify's own results are still on screen behind us.
          render(container, [], term);
          console.warn('[storefront-search]', err.message);
        }
      }, DEBOUNCE_MS);
    });

    // Warm the index on first focus so the first query feels instant.
    input.addEventListener('focus', () => { getIndex().catch(() => {}); }, { once: true });
  }

  function init() {
    document.querySelectorAll('predictive-search-component').forEach(mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
